import React from 'react';
import { Link } from 'react-router-dom';
import { useFavorites } from '../../../contexts/FavoritesContext';
import { buildAssetUrl, getPlaceholderImage } from '../../../config/constants';

interface FavoriteBook {
  id: number;
  userId: number;
  bookId: number;
  createdAt: string;
  Book: {
    id: number;
    title: string;
    author: string;
    coverImage?: string;
    description?: string;
    category?: string;
    publishedYear?: number;
    fileName?: string;
    fileType?: string;
  };
}

const FavoriteBooksTab: React.FC = () => {
  const { favorites, loading, removeFromFavorites } = useFavorites();

  const handleRemoveFromFavorites = async (bookId: number) => {
    try {
      await removeFromFavorites(bookId);
    } catch (error) {
      console.error('Eroare la eliminarea din favorite:', error);
    }
  };

  const getBookCoverImage = (book: any) => {
    if (book.coverImage) {
      return buildAssetUrl(book.coverImage);
    }
    return getPlaceholderImage(book.title, 200, 300);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (favorites.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">Nicio carte favorită</h3>
        <p className="mt-1 text-sm text-gray-500">
          Nu ai adăugat încă nicio carte la favorite.
        </p>
        <div className="mt-6">
          <Link
            to="/books"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Explorează cărțile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 mb-4">
        Cărțile tale favorite ({favorites.length})
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {favorites.map((favorite) => {
          const book: any = favorite.Book;
          return (
            <div key={favorite.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="flex">
                <div className="flex-shrink-0">
                  <img
                    className="h-32 w-24 object-cover"
                    src={getBookCoverImage(book)}
                    alt={book.title}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = getPlaceholderImage(book.title, 200, 300);
                    }}
                  />
                </div>
                <div className="flex-1 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/books/${book.id}`}
                        className="text-sm font-medium text-gray-900 hover:text-blue-600 line-clamp-2"
                      >
                        {book.title}
                      </Link>
                      <p className="text-sm text-gray-500 mt-1">{book.author}</p>
                      {book.category && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                          {book.category}
                        </span>
                      )}
                      {book.publishedYear && (
                        <p className="text-xs text-gray-400 mt-1">
                          Publicat în {book.publishedYear}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveFromFavorites(book.id)}
                      className="ml-2 text-red-400 hover:text-red-600 transition-colors"
                      title="Elimină din favorite"
                    >
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="mt-3 flex justify-between items-center">
                    <p className="text-xs text-gray-400">
                      Adăugat pe {new Date(favorite.createdAt).toLocaleDateString('ro-RO')}
                    </p>
                    <Link
                      to={`/books/${book.id}`}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Vezi detalii →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FavoriteBooksTab; 
